import { AxiosError } from "axios";
import { createServerApiClient } from "../server-base-client";
import {
  PaginatedShopProducts,
  ShopAssetFilter,
  ShopCollectionTree,
  ShopFilterOptions,
  ShopProduct,
  ShopTaxonomyValue,
} from "./shop.types";

const SHOP_ASSETS_PATH = "v1/shop/assets";
const BATCH_MAX_IDS = 50;
const HTTP_NOT_FOUND = 404;

/**
 * Server-only client for the read-only shop assets API. Authenticates with the
 * minted, DB-backed admin key in `ASSETS_API_KEY` (already `base64(id:secret)`,
 * so it is sent verbatim as `Authorization: Basic <key>` — not re-encoded).
 * The key is a top-tier secret: never `NEXT_PUBLIC_`, never logged.
 */
async function getShopApiClient() {
  const apiKey = process.env.ASSETS_API_KEY;
  if (!apiKey) {
    throw new Error("ASSETS_API_KEY is not configured");
  }
  const client = await createServerApiClient();
  client.defaults.headers.common["Authorization"] = `Basic ${apiKey}`;
  return client;
}

function buildFilterParams(filter: ShopAssetFilter): URLSearchParams {
  const params = new URLSearchParams();

  const setIfPresent = (
    key: string,
    value: string | number | boolean | undefined,
  ) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  };

  setIfPresent("collection", filter.collection);
  for (const [taxonomyKey, valueId] of Object.entries(
    filter.taxonomies ?? {},
  )) {
    params.set(`taxonomies[${taxonomyKey}]`, valueId);
  }
  setIfPresent("taxonomiesRecursive", filter.taxonomiesRecursive);
  for (const [name, values] of Object.entries(filter.attributes ?? {})) {
    if (values.length > 0) params.set(`attr.${name}`, values.join(","));
  }
  setIfPresent("priceMin", filter.priceMin);
  setIfPresent("priceMax", filter.priceMax);
  setIfPresent("vendor", filter.vendor);
  setIfPresent("retailer", filter.retailer);
  setIfPresent("partner", filter.partner);
  setIfPresent("search", filter.search);
  setIfPresent("sortBy", filter.sortBy);
  setIfPresent("sortDirection", filter.sortDirection);
  setIfPresent("page", filter.page);
  setIfPresent("limit", filter.limit);

  return params;
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/** ACTIVE collection browse tree rooted at `root` (defaults to the guide root). */
export async function getShopCollectionTree(
  root?: string,
): Promise<ShopCollectionTree> {
  const client = await getShopApiClient();
  const { data } = await client.get<ShopCollectionTree>(
    `${SHOP_ASSETS_PATH}/collections`,
    { params: root ? { root } : undefined },
  );
  return data;
}

/** ACTIVE value browse tree for a taxonomy. */
export async function getShopTaxonomyTree(
  key: string,
): Promise<ShopTaxonomyValue[]> {
  const client = await getShopApiClient();
  const { data } = await client.get<ShopTaxonomyValue[]>(
    `${SHOP_ASSETS_PATH}/taxonomies/${encodeURIComponent(key)}`,
  );
  return data;
}

/** Paginated product list for a leaf's collection / taxonomy filter. */
export async function listShopAssets(
  filter: ShopAssetFilter = {},
): Promise<PaginatedShopProducts> {
  const client = await getShopApiClient();
  const { data } = await client.get<PaginatedShopProducts>(SHOP_ASSETS_PATH, {
    params: buildFilterParams(filter),
  });
  return data;
}

/** Distinct facet values for the resolved collection / taxonomy filter. */
export async function getShopFilterOptions(
  filter: ShopAssetFilter = {},
): Promise<ShopFilterOptions> {
  const client = await getShopApiClient();
  const params = new URLSearchParams();
  if (filter.collection) params.set("collection", filter.collection);
  for (const [taxonomyKey, valueId] of Object.entries(
    filter.taxonomies ?? {},
  )) {
    params.set(`taxonomies[${taxonomyKey}]`, valueId);
  }
  if (filter.taxonomiesRecursive) params.set("taxonomiesRecursive", "true");
  const { data } = await client.get<ShopFilterOptions>(
    `${SHOP_ASSETS_PATH}/filter-options`,
    { params },
  );
  return data;
}

/** Batch-hydrate products by ID, preserving input order, dropping misses. */
export async function getShopAssetsByIds(
  ids: string[],
  collection?: string,
): Promise<ShopProduct[]> {
  if (ids.length === 0) return [];
  const client = await getShopApiClient();

  const responses = await Promise.all(
    chunk(ids, BATCH_MAX_IDS).map((batch) => {
      const params = new URLSearchParams({ ids: batch.join(",") });
      if (collection) params.set("collection", collection);
      return client.get<{ data: ShopProduct[] }>(SHOP_ASSETS_PATH, { params });
    }),
  );

  const productsById = new Map(
    responses.flatMap((r) => r.data.data).map((p) => [p.id, p]),
  );
  return ids
    .map((id) => productsById.get(id))
    .filter((product): product is ShopProduct => product !== undefined);
}

/** Single product by ID; returns null when it is not in the collection (404). */
export async function getShopAsset(
  id: string,
  collection?: string,
): Promise<ShopProduct | null> {
  const client = await getShopApiClient();
  try {
    const { data } = await client.get<ShopProduct>(
      `${SHOP_ASSETS_PATH}/${id}`,
      { params: collection ? { collection } : undefined },
    );
    return data;
  } catch (error) {
    if (
      error instanceof AxiosError &&
      error.response?.status === HTTP_NOT_FOUND
    ) {
      return null;
    }
    throw error;
  }
}
